import type { WebGLRenderer } from 'three';

export interface PerfSnapshot {
  fps: number;
  frameMs: number;
  physicsMs: number;
  drawCalls: number;
  triangles: number;
  programs: number;
  textures: number;
  geometries: number;
  memoryMb: number;
  pixelRatio: number;
  renderScale: number;
  quality: string;
}

interface MemoryCapableWindow {
  memory?: { usedJSHeapSize: number };
}

/**
 * Sammelt Frame-Statistiken. Schreibt bewusst in ein einziges, wiederverwendetes
 * Objekt - kein Allozieren pro Frame, kein React-State im 60-fps-Takt.
 */
export class PerfMonitor {
  readonly snapshot: PerfSnapshot = {
    fps: 0,
    frameMs: 0,
    physicsMs: 0,
    drawCalls: 0,
    triangles: 0,
    programs: 0,
    textures: 0,
    geometries: 0,
    memoryMb: 0,
    pixelRatio: 1,
    renderScale: 1,
    quality: '-',
  };

  /** Geglaettete fps fuer die dynamische Aufloesungsskalierung. */
  smoothedFps = 60;

  private frames = 0;
  private accum = 0;
  private published = false;
  private frameMsAccum = 0;
  private physicsMsAccum = 0;

  beginFrame(): number {
    return performance.now();
  }

  addPhysicsTime(ms: number): void {
    this.physicsMsAccum += ms;
  }

  endFrame(startedAt: number, dt: number, renderer: WebGLRenderer): void {
    const frameMs = performance.now() - startedAt;
    this.frameMsAccum += frameMs;
    this.frames += 1;
    this.accum += dt;

    // Sofort geglaettet, damit die Aufloesungsskalierung schnell reagiert.
    const instantFps = dt > 0 ? 1 / dt : 60;
    this.smoothedFps += (instantFps - this.smoothedFps) * 0.05;

    // Erster Frame sofort, danach viermal pro Sekunde: der Overlay ist beim
    // Aufklappen nie leer, kostet aber trotzdem fast nichts.
    if (this.accum >= 0.25 || !this.published) {
      this.published = true;
      const s = this.snapshot;
      s.fps = Math.round(this.frames / this.accum);
      s.frameMs = Math.round((this.frameMsAccum / this.frames) * 100) / 100;
      s.physicsMs = Math.round((this.physicsMsAccum / this.frames) * 100) / 100;
      s.drawCalls = renderer.info.render.calls;
      s.triangles = renderer.info.render.triangles;
      s.programs = renderer.info.programs?.length ?? 0;
      s.textures = renderer.info.memory.textures;
      s.geometries = renderer.info.memory.geometries;
      s.pixelRatio = Math.round(renderer.getPixelRatio() * 100) / 100;

      const mem = (performance as unknown as MemoryCapableWindow).memory;
      s.memoryMb = mem ? Math.round(mem.usedJSHeapSize / 1048576) : 0;

      this.frames = 0;
      this.accum = 0;
      this.frameMsAccum = 0;
      this.physicsMsAccum = 0;
    }
  }
}
